import { motion } from "framer-motion";
import { GraduationCap, Award, Stethoscope, MailCheck } from "lucide-react";
import { CLINIC } from "../data/content";
import { Kicker, TiltCard } from "./fx";

const EASE = [0.22, 1, 0.36, 1];

const CARDS = [
  {
    icon: GraduationCap,
    title: "Lucknow × Kolkata Pedigree",
    desc: "B.D.S (Lucknow), M.D.S (Kolkata) and a Fellowship of the Academy of General Education.",
  },
  {
    icon: Stethoscope,
    title: "15 Years of Mastery",
    desc: "A decade and a half of endodontic and multi-speciality clinical excellence.",
  },
  {
    icon: Award,
    title: "Painless Rotary Protocol",
    desc: "Single-sitting rotary endodontics and microscope-guided precision, minus the fear.",
  },
  {
    icon: MailCheck,
    title: "Personal Oversight",
    desc: "Every online booking is reviewed personally at drswagatndc@gmail.com.",
  },
];

export default function Doctor() {
  return (
    <section
      id="doctor"
      data-testid="doctor-profile-section"
      className="grain relative overflow-hidden border-t border-white/10 bg-navy-deep py-24 text-ivory sm:py-32"
    >
      <div
        aria-hidden
        className="pointer-events-none absolute -left-32 top-1/4 h-[480px] w-[480px] rounded-full bg-toothcyan/10 blur-3xl"
      />
      <div
        aria-hidden
        className="pointer-events-none absolute -right-24 bottom-0 h-[420px] w-[420px] rounded-full bg-amber-500/10 blur-3xl"
      />

      <div className="relative mx-auto grid max-w-7xl grid-cols-1 items-center gap-14 px-5 sm:px-8 lg:grid-cols-12">
        <motion.div
          initial={{
            opacity: 0,
            clipPath: "polygon(0 10%, 100% 0%, 100% 90%, 0 100%)",
          }}
          whileInView={{ opacity: 1, clipPath: "polygon(0 0%, 100% 0%, 100% 100%, 0 100%)" }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 1.1, ease: EASE }}
          className="relative lg:col-span-5"
        >
          <div className="relative mx-auto max-w-sm">
            <div
              aria-hidden
              className="absolute -inset-4 rounded-[2.5rem] border border-ember/40"
            />
            <div
              aria-hidden
              className="absolute -inset-4 translate-x-4 translate-y-4 rounded-[2.5rem] border border-white/10"
            />
            <div className="relative overflow-hidden rounded-[2.5rem] shadow-[0_30px_80px_rgba(0,0,0,0.7)]">
              <img
                src={CLINIC.doctorPhoto}
                alt="Dr. Swagat Panda in clinical attire"
                className="aspect-[4/5] w-full object-cover object-top"
                data-testid="doctor-portrait-image"
              />
              <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-[#070B12]/95 via-[#070B12]/40 to-transparent px-6 pb-8 pt-24">
                <div className="font-serif text-xl font-semibold">
                  {CLINIC.doctor}
                </div>
                <div className="mt-1 font-mono text-[10px] uppercase tracking-[0.22em] text-ember">
                  Endodontist · 15 Years of Excellence
                </div>
              </div>
            </div>
            <div className="absolute -right-3 -top-3 rotate-3 rounded-full bg-gradient-to-r from-ember to-amber-500 px-4 py-2 shadow-[0_0_20px_rgba(232,178,92,0.4)]">
              <span className="font-mono text-[9px] font-bold uppercase tracking-[0.18em] text-[#070B12]">
                Chief Endodontist
              </span>
            </div>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 34 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.9, delay: 0.15, ease: EASE }}
          className="lg:col-span-7"
        >
          <Kicker>Meet The Specialist</Kicker>
          <h2 className="font-serif text-3xl font-light tracking-tight text-white sm:text-5xl">
            {CLINIC.doctorHonorific}
          </h2>

          <div className="mt-5 flex flex-wrap gap-2.5">
            {CLINIC.credentials.map((c) => (
              <span
                key={c}
                data-testid={`doctor-credential-${c.split(" ")[0].replace(/[^a-z]/gi, "").toLowerCase()}`}
                className="rounded-full border border-ember/30 bg-ember/10 px-4 py-1.5 font-mono text-[11px] uppercase tracking-[0.14em] text-ivory/90"
              >
                {c}
              </span>
            ))}
          </div>

          <motion.blockquote
            initial={{ opacity: 0, x: -20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, delay: 0.25, ease: EASE }}
            className="mt-8 border-l-2 border-ember/60 pl-5"
          >
            <p className="font-serif text-lg italic leading-relaxed text-slate-200 sm:text-xl">
              <span className="mr-1 font-serif text-3xl leading-none text-ember">
                “
              </span>
              A tooth saved is a smile preserved. My chair is a studio, not a
              surgery — every treatment is crafted, never rushed.
            </p>
            <footer className="mt-3 font-mono text-[10px] uppercase tracking-[0.22em] text-mist">
              — Dr. Swagat Panda · Chief Endodontist
            </footer>
          </motion.blockquote>

          <div className="mt-9 grid gap-4 sm:grid-cols-2">
            {CARDS.map((c, i) => (
              <motion.div
                key={c.title}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: 0.2 + i * 0.1, ease: EASE }}
              >
                <TiltCard className="group h-full rounded-2xl border border-white/10 bg-white/[0.04] p-5 backdrop-blur transition-colors duration-300 hover:border-ember/40 hover:bg-white/[0.06] hover:shadow-[0_0_30px_rgba(232,178,92,0.08)]">
                  <span className="flex h-9 w-9 items-center justify-center rounded-xl bg-ember/15 text-ember transition-colors duration-300 group-hover:bg-ember group-hover:text-[#070B12]">
                    <c.icon className="h-4 w-4" />
                  </span>
                  <div className="mt-3.5 text-sm font-bold text-ivory">
                    {c.title}
                  </div>
                  <p className="mt-1.5 text-xs leading-relaxed text-ivory/60">
                    {c.desc}
                  </p>
                </TiltCard>
              </motion.div>
            ))}
          </div>
        </motion.div>
      </div>
    </section>
  );
}
